import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';
import { useNotification } from '../../contexts/NotificationContext';

const AdminQuotationsTab = ({ quotations, loading, updateStatus }) => {
  const { toast } = useNotification();
  const [resending, setResending] = useState(null);
  const [message, setMessage] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-green-100 text-green-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'error_email': return 'bg-red-100 text-red-800';
      case 'error_pdf': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'sent': return 'Enviada';
      case 'contacted': return 'Contactado';
      case 'completed': return 'Completado';
      case 'error_email': return 'Error Email';
      case 'error_pdf': return 'Error PDF';
      default: return status;
    }
  };

  const handleResend = async (quotationId) => {
    setResending(quotationId);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/quotations/${quotationId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('✅ Cotización reenviada exitosamente');
      setTimeout(() => setMessage(''), 3000);
      window.location.reload();
    } catch (error) {
      console.error('Error resending quotation:', error);
      setMessage('❌ Error al reenviar cotización');
    } finally {
      setResending(null);
    }
  };

  const handleDownloadPDF = async (quotationId, folio) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/quotations/${quotationId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `COT-${folio || 'XXX'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar PDF');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-300 mx-auto"></div>
        <p className="mt-2 text-gray-600">Cargando cotizaciones...</p>
      </div>
    );
  }

  if (quotations.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="mail" className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No hay cotizaciones pendientes</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg ${message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}
      
      {quotations.map((quotation) => (
        <div key={quotation.id} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-semibold text-black">{quotation.company}</h3>
              <p className="text-gray-600">{quotation.contact_person}</p>
              {quotation.folio && (
                <p className="text-sm font-mono text-blue-600 mt-1">COT-{quotation.folio}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(quotation.status)}`}>
                {getStatusText(quotation.status)}
              </span>
              {quotation.email_sent && (
                <span className="text-xs text-green-600">
                  ✓ Email enviado {quotation.email_sent_at ? new Date(quotation.email_sent_at).toLocaleDateString() : ''}
                </span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{quotation.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Teléfono</p>
              <p className="font-medium">{quotation.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Cantidad Estimada</p>
              <p className="font-medium">{quotation.estimated_cards}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Fecha</p>
              <p className="font-medium">{new Date(quotation.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          
          {quotation.comments && (
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-500">Comentarios</p>
              <p className="text-sm">{quotation.comments}</p>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2">
            {quotation.folio && (
              <button
                onClick={() => handleDownloadPDF(quotation.id, quotation.folio)}
                className="bg-blue-100 text-blue-800 hover:bg-blue-200 text-sm px-3 py-1 rounded-lg transition-colors"
              >
                📄 Ver PDF
              </button>
            )}
            <button
              onClick={() => handleResend(quotation.id)}
              disabled={resending === quotation.id}
              className="bg-purple-100 text-purple-800 hover:bg-purple-200 text-sm px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
            >
              {resending === quotation.id ? 'Enviando...' : '📧 Reenviar'}
            </button>
            <button
              onClick={() => updateStatus(quotation.id, 'contacted')}
              disabled={quotation.status === 'contacted' || quotation.status === 'completed'}
              className="btn-secondary text-sm px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Marcar como Contactado
            </button>
            <button
              onClick={() => updateStatus(quotation.id, 'completed')}
              disabled={quotation.status === 'completed'}
              className="btn-primary text-sm px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Marcar como Completado
            </button>
            <a
              href={`mailto:${quotation.email}?subject=Cotización CONTACT - ${quotation.company}&body=Estimado(a) ${quotation.contact_person},%0D%0A%0D%0AGracias por su interés en nuestras soluciones de networking digital.%0D%0A%0D%0ASaludos cordiales,%0D%0AEquipo CONTACT`}
              className="btn-secondary text-sm px-3 py-1"
            >
              Enviar Email
            </a>
          </div>
        </div>
      ))}
    </div>
  );
};

// Product Management Panel Component

export default AdminQuotationsTab;
